<template>
  <div class="admin-share-campaigns">
    <div class="page-header">
      <h2>分享活动管理</h2>
      <el-button type="primary" icon="el-icon-plus" @click="showCreateDialog">创建活动</el-button>
    </div>

    <!-- 桌面端：表格 -->
    <el-table :data="campaigns" v-loading="loading" stripe class="desktop-table">
      <el-table-column prop="id" label="ID" width="60" />
      <el-table-column prop="name" label="活动名称" min-width="150" />
      <el-table-column prop="rewardType" label="奖励类型" width="100">
        <template slot-scope="{row}">
          <el-tag :type="rewardTypeTag(row.rewardType)">{{ rewardTypeText(row.rewardType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rewardValue" label="奖励金额" width="100">
        <template slot-scope="{row}">¥{{ row.rewardValue }}</template>
      </el-table-column>
      <el-table-column prop="rewardProductName" label="奖品名称" min-width="120" />
      <el-table-column prop="requireFirstOrder" label="需首单" width="80">
        <template slot-scope="{row}">{{ row.requireFirstOrder ? '是' : '否' }}</template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="{row}">
          <el-tag :type="row.status === 'ACTIVE' ? 'success' : 'info'">
            {{ row.status === 'ACTIVE' ? '上架' : '下架' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="startDate" label="开始日期" width="120" />
      <el-table-column prop="endDate" label="结束日期" width="120" />
      <el-table-column label="操作" width="280" fixed="right">
        <template slot-scope="{row}">
          <el-button size="mini" @click="editCampaign(row)">编辑</el-button>
          <el-button
            size="mini"
            :type="row.status === 'ACTIVE' ? 'warning' : 'success'"
            @click="toggleStatus(row)"
          >
            {{ row.status === 'ACTIVE' ? '下架' : '上架' }}
          </el-button>
          <el-button size="mini" type="danger" @click="deleteCampaign(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 移动端：卡片列表 -->
    <div class="mobile-cards" v-loading="loading">
      <div
        v-for="item in campaigns"
        :key="item.id"
        class="campaign-card"
        :class="{ inactive: item.status !== 'ACTIVE' }"
      >
        <div class="card-top">
          <div class="card-title">{{ item.name }}</div>
          <el-tag :type="item.status === 'ACTIVE' ? 'success' : 'info'" size="mini">
            {{ item.status === 'ACTIVE' ? '上架' : '下架' }}
          </el-tag>
        </div>
        <div class="card-info">
          <div class="info-row">
            <span class="info-label">奖励类型</span>
            <el-tag :type="rewardTypeTag(item.rewardType)" size="mini">{{ rewardTypeText(item.rewardType) }}</el-tag>
          </div>
          <div class="info-row">
            <span class="info-label">奖励金额</span>
            <span class="info-value price">¥{{ item.rewardValue }}</span>
          </div>
          <div class="info-row" v-if="item.rewardProductName">
            <span class="info-label">奖品名称</span>
            <span class="info-value">{{ item.rewardProductName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">需首单</span>
            <span class="info-value">{{ item.requireFirstOrder ? '是' : '否' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">每人限次</span>
            <span class="info-value">{{ item.maxRewardsPerSharer === 0 ? '不限' : item.maxRewardsPerSharer + '次' }}</span>
          </div>
          <div class="info-row" v-if="item.startDate">
            <span class="info-label">有效期</span>
            <span class="info-value">{{ item.startDate }} ~ {{ item.endDate || '长期' }}</span>
          </div>
        </div>
        <div class="card-actions">
          <el-button size="small" plain @click="editCampaign(item)">✏️ 编辑</el-button>
          <el-button
            size="small"
            :type="item.status === 'ACTIVE' ? 'warning' : 'success'"
            plain
            @click="toggleStatus(item)"
          >
            {{ item.status === 'ACTIVE' ? '⏸️ 下架' : '▶️ 上架' }}
          </el-button>
        </div>
        <div class="card-actions card-actions-secondary">
          <el-button size="small" type="danger" plain @click="deleteCampaign(item)">🗑️ 删除</el-button>
        </div>
      </div>
      <el-empty v-if="!loading && campaigns.length === 0" description="暂无活动，点击上方按钮创建" />
    </div>

    <!-- 创建/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      @close="resetForm"
    >
      <el-form :model="form" :rules="rules" ref="campaignForm" label-width="100px">
        <el-form-item label="活动名称" prop="name">
          <el-input v-model="form.name" placeholder="例如：推荐有礼活动" />
        </el-form-item>
        <el-form-item label="活动说明" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="活动说明" />
        </el-form-item>
        <el-form-item label="奖励类型" prop="rewardType">
          <el-select v-model="form.rewardType" style="width: 100%;">
            <el-option label="现金" value="CASH" />
            <el-option label="优惠券" value="COUPON" />
            <el-option label="商品" value="PRODUCT" />
          </el-select>
        </el-form-item>
        <el-form-item label="奖励金额" prop="rewardValue">
          <el-input-number v-model="form.rewardValue" :min="0" :precision="2" :step="10" />
        </el-form-item>
        <el-form-item label="奖品名称">
          <el-input v-model="form.rewardProductName" placeholder="例如：iPhone 16、现金红包" />
        </el-form-item>
        <el-form-item label="需首单">
          <el-switch v-model="form.requireFirstOrder" />
        </el-form-item>
        <el-form-item label="每人限次">
          <el-input-number v-model="form.maxRewardsPerSharer" :min="0" :step="1" />
          <span style="font-size: 12px; color: #999; margin-left: 8px;">0=不限</span>
        </el-form-item>
        <el-form-item label="开始日期">
          <el-date-picker v-model="form.startDate" type="date" value-format="yyyy-MM-dd" />
        </el-form-item>
        <el-form-item label="结束日期">
          <el-date-picker v-model="form.endDate" type="date" value-format="yyyy-MM-dd" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'AdminShareCampaigns',
  data() {
    return {
      campaigns: [],
      loading: false,
      dialogVisible: false,
      dialogTitle: '创建活动',
      submitLoading: false,
      isEdit: false,
      form: {
        id: null,
        name: '',
        description: '',
        rewardType: 'CASH',
        rewardValue: 50,
        rewardProductName: '',
        requireFirstOrder: true,
        maxRewardsPerSharer: 0,
        startDate: '',
        endDate: ''
      },
      rules: {
        name: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
        rewardType: [{ required: true, message: '请选择奖励类型', trigger: 'change' }],
        rewardValue: [{ required: true, message: '请输入奖励金额', trigger: 'blur' }]
      }
    };
  },
  created() {
    this.loadCampaigns();
  },
  methods: {
    async loadCampaigns() {
      this.loading = true;
      try {
        const response = await this.$api.get('/admin/share/campaigns');
        this.campaigns = response.data || response || [];
      } catch (error) {
        console.error('加载活动列表失败:', error);
        this.$message.error('加载活动列表失败');
      } finally {
        this.loading = false;
      }
    },

    showCreateDialog() {
      this.isEdit = false;
      this.dialogTitle = '创建活动';
      this.dialogVisible = true;
    },

    editCampaign(row) {
      this.isEdit = true;
      this.dialogTitle = '编辑活动';
      this.form = { ...row };
      this.dialogVisible = true;
    },

    resetForm() {
      this.form = {
        id: null,
        name: '',
        description: '',
        rewardType: 'CASH',
        rewardValue: 50,
        rewardProductName: '',
        requireFirstOrder: true,
        maxRewardsPerSharer: 0,
        startDate: '',
        endDate: ''
      };
      if (this.$refs.campaignForm) {
        this.$refs.campaignForm.resetFields();
      }
    },

    async submitForm() {
      this.$refs.campaignForm.validate(async (valid) => {
        if (!valid) return;

        this.submitLoading = true;
        try {
          const url = this.isEdit
            ? `/admin/share/campaigns/${this.form.id}`
            : '/admin/share/campaigns';
          const method = this.isEdit ? 'put' : 'post';

          await this.$api[method](url, this.form);
          this.$message.success(this.isEdit ? '更新成功' : '创建成功');
          this.dialogVisible = false;
          this.loadCampaigns();
        } catch (error) {
          console.error('保存活动失败:', error);
          this.$message.error('保存活动失败');
        } finally {
          this.submitLoading = false;
        }
      });
    },

    async toggleStatus(row) {
      const newStatus = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      try {
        await this.$api.put(`/admin/share/campaigns/${row.id}`, { status: newStatus });
        this.$message.success(newStatus === 'ACTIVE' ? '已上架' : '已下架');
        this.loadCampaigns();
      } catch (error) {
        console.error('更新状态失败:', error);
        this.$message.error('更新状态失败');
      }
    },

    async deleteCampaign(row) {
      try {
        await this.$confirm(`确认删除活动「${row.name}」？`, '提示', {
          type: 'warning'
        });
        await this.$api.delete(`/admin/share/campaigns/${row.id}`);
        this.$message.success('已删除');
        this.loadCampaigns();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除活动失败:', error);
          this.$message.error('删除失败');
        }
      }
    },

    rewardTypeTag(type) {
      const map = { CASH: '', COUPON: 'warning', PRODUCT: 'success' };
      return map[type] || '';
    },

    rewardTypeText(type) {
      const map = { CASH: '现金', COUPON: '优惠券', PRODUCT: '商品' };
      return map[type] || type;
    }
  }
};
</script>

<style scoped>
.admin-share-campaigns {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
}

/* ===== 移动端卡片 ===== */
.mobile-cards {
  display: none;
}

@media (max-width: 768px) {
  .admin-share-campaigns {
    padding: 12px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .page-header h2 {
    font-size: 18px;
  }

  .page-header .el-button {
    width: 100%;
  }

  /* 隐藏桌面表格，显示卡片 */
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .campaign-card {
    background: #fff;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    transition: transform 0.15s;
  }

  .campaign-card.inactive {
    opacity: 0.6;
  }

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .card-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    flex: 1;
    margin-right: 8px;
    line-height: 1.3;
  }

  .card-info {
    background: #f8f8fa;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 13px;
  }

  .info-row + .info-row {
    border-top: 1px solid #eee;
  }

  .info-label {
    color: #888;
    font-size: 12px;
  }

  .info-value {
    color: #333;
    font-weight: 500;
  }

  .info-value.price {
    color: #e74c3c;
    font-size: 15px;
    font-weight: 700;
  }

  .card-actions {
    display: flex;
    gap: 8px;
  }

  .card-actions-secondary {
    margin-top: 4px;
  }

  .card-actions .el-button {
    flex: 1;
  }
}
</style>
